import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ReactSelect, { type StylesConfig } from 'react-select';
import { toast } from 'react-toastify';
import classes from './QualityReportPage.module.scss';
import HeaderAnalyticsTables from '../../../components/Admin/HeaderAnalyticsAndReports/HeaderAnalyticsTables';
import BranchQueueFilter from '../../../components/Admin/BranchQueueFilter/BranchQueueFilter';
import QualityReportTable from '../../../components/Admin/Tables/AnalyticsAndReports/QualityReportTable/QualityReportTable';
import { useAdminQueueIdsFilter } from '../../../hooks/Admin/useAdminQueueIdsFilter';
import { useGetBranches } from '../../../hooks/Admin/useGetBranches';
import { useGetEmployeesForReportFilter } from '../../../hooks/Admin/AnalyticsAndReportsTables/EmployeesReport/useGetEmployeesForReportFilter';
import { useGetQualityReport } from '../../../hooks/Admin/AnalyticsAndReportsTables/QualityReport/useGetQualityReport';
import { currentDate } from '../../../utils/Admin/AnalyticsAndReports/formatAnalyticAndReportsDate';
import { saveAsCSV } from '../../../utils/Admin/AnalyticsAndReports/saveAsCSV';
import { transliterateForFilename } from '../../../utils/Admin/AnalyticsAndReports/transliterateForFilename';
import {
  formatQualityReportAverage,
  formatQualityReportCount
} from '../../../utils/Admin/AnalyticsAndReports/formatQualityReportValue';
import { QualityReportHeader } from '../../../types/enums';
import { BranchAPI } from '../../../constants/API';
import { commonHeaders } from '../../../constants/HeadersAPI';
import type { BranchQueueItemDto } from '../../../hooks/Admin/WorkingPlacesTable/useGetQueuesForCurrentUser';
import { QualityReportEmployeeRow } from '../../../types/Admin/QualityReport';

const tabsList = ['Период'];
const isActiveButtonCSV = true;

type EmployeeOption = { label: string; value: number | null };

const selectStyles: StylesConfig<EmployeeOption, false> = {
  container: (base) => ({ ...base, width: 250, maxWidth: 250 }),
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderRadius: 8,
    borderColor: state.isFocused ? '#5aa2dc' : 'var(--gray-border)',
    boxShadow: 'none',
    backgroundColor: 'var(--white)',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-roboto-400)',
    fontSize: 15,
    ':hover': {
      borderColor: state.isDisabled ? 'var(--gray-border)' : '#5aa2dc'
    }
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 15, lineHeight: '20px' }),
  valueContainer: (base) => ({ ...base, padding: '0 10px' }),
  singleValue: (base) => ({
    ...base,
    color: '#404040',
    fontSize: 15,
    lineHeight: '20px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%'
  }),
  placeholder: (base) => ({ ...base, color: '#404040', fontSize: 15, lineHeight: '20px' }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: 6 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, width: 250, maxWidth: 250, fontSize: 15 }),
  menuList: (base) => ({ ...base, maxHeight: 240, fontSize: 15 }),
  option: (base, state) => ({
    ...base,
    fontSize: 15,
    lineHeight: '20px',
    cursor: 'pointer',
    backgroundColor: state.isFocused ? 'rgba(0, 0, 0, 0.04)' : 'var(--white)',
    color: '#404040'
  })
};

const toCsvRow = (row: QualityReportEmployeeRow) => ({
  employeeName: row.employeeName,
  ratingsCount: formatQualityReportCount(row.ratingsCount),
  averageRating: formatQualityReportAverage(row.averageRating),
  score1: formatQualityReportCount(row.score1),
  score2: formatQualityReportCount(row.score2),
  score3: formatQualityReportCount(row.score3),
  score4: formatQualityReportCount(row.score4),
  score5: formatQualityReportCount(row.score5)
});

const QualityReportPage: FC = () => {
  const [activeTab, setActiveTab] = useState(tabsList[0]);
  const [selectedStartDate, setSelectedStartDate] = useState(currentDate);
  const [selectedEndDate, setSelectedEndDate] = useState(currentDate);
  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [selectedBranchName, setSelectedBranchName] = useState('');
  const [selectedQueueName, setSelectedQueueName] = useState('');
  const selectedBranchQueueId = filterQueueIds.length ? filterQueueIds[0] : null;
  const isQueueSelected = selectedBranchQueueId != null;
  const isDateRangeInvalid = selectedStartDate > selectedEndDate;
  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const { branches } = useGetBranches();
  const prevQueueIdRef = useRef<number | null>(null);
  const menuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;

  const { employees: employeesForFilter, isLoading: isLoadingEmployeesForFilter } =
    useGetEmployeesForReportFilter();

  useEffect(() => {
    if (!selectedBranchQueueId || filterBranchIds.length === 0) {
      setSelectedBranchName('');
      setSelectedQueueName('');
      return;
    }

    const branchId = filterBranchIds[0];
    const branch = branches.find((b) => b.id === branchId);
    setSelectedBranchName(branch?.name ?? branch?.code ?? '');

    let cancelled = false;
    axios
      .get<BranchQueueItemDto[]>(BranchAPI.getQueues(branchId), commonHeaders())
      .then(({ data }) => {
        if (cancelled) return;
        const queue = data.find((q) => q.id === selectedBranchQueueId);
        setSelectedQueueName(queue?.queueName ?? '');
      })
      .catch(() => {
        if (!cancelled) setSelectedQueueName('');
      });

    return () => {
      cancelled = true;
    };
  }, [selectedBranchQueueId, filterBranchIds, branches]);

  useEffect(() => {
    const prev = prevQueueIdRef.current;
    if (prev !== null && selectedBranchQueueId !== null && prev !== selectedBranchQueueId) {
      setSelectedEmployeeId(null);
    }
    prevQueueIdRef.current = selectedBranchQueueId;
  }, [selectedBranchQueueId]);

  const employeesInSelectedQueue = selectedBranchQueueId
    ? employeesForFilter.filter((e) => e.queueIds.includes(selectedBranchQueueId))
    : [];

  const employeeOptions: EmployeeOption[] = useMemo(() => {
    const opts = employeesInSelectedQueue.map((e) => ({ label: e.label, value: e.id }));
    return [{ label: 'Все сотрудники', value: null }, ...opts];
  }, [employeesInSelectedQueue]);

  const selectedEmployeeOption: EmployeeOption | null = useMemo(() => {
    if (selectedEmployeeId == null) return employeeOptions[0] ?? null;
    return employeeOptions.find((o) => o.value === selectedEmployeeId) ?? null;
  }, [employeeOptions, selectedEmployeeId]);

  const selectedDate = {
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    branchQueueId: selectedBranchQueueId,
    employeeId: selectedEmployeeId
  };

  const {
    qualityReportData,
    refetchQualityReport,
    isLoadingQualityReport
  } = useGetQualityReport(selectedDate, isQueueSelected && !isDateRangeInvalid);

  useEffect(() => {
    if (isQueueSelected && !isDateRangeInvalid) {
      refetchQualityReport();
    }
  }, [
    selectedStartDate,
    selectedEndDate,
    selectedBranchQueueId,
    selectedEmployeeId,
    isQueueSelected,
    isDateRangeInvalid,
    refetchQualityReport
  ]);

  const handleSaveAsCSV = () => {
    try {
      if (!qualityReportData) {
        saveAsCSV([], QualityReportHeader, {
          filename: `quality_report_${transliterateForFilename(selectedBranchName || 'Branch')}_${transliterateForFilename(selectedQueueName || 'Queue')}_${selectedStartDate}_${selectedEndDate}.csv`
        });
        return;
      }

      const rows = qualityReportData.hasRatings
        ? [
            ...qualityReportData.employees.map(toCsvRow),
            toCsvRow(qualityReportData.totals)
          ]
        : [];

      saveAsCSV(rows, QualityReportHeader, {
        filename: `quality_report_${transliterateForFilename(selectedBranchName || 'Branch')}_${transliterateForFilename(selectedQueueName || 'Queue')}_${selectedStartDate}_${selectedEndDate}.csv`
      });
    } catch {
      toast.error('Не удалось выполнить экспорт. Попробуйте позже');
    }
  };

  return (
    <div className={classes.container}>
      <HeaderAnalyticsTables
        tabs={tabsList}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleSaveAsCSV={handleSaveAsCSV}
        isActiveButtonCSV={isActiveButtonCSV}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedStartDate={setSelectedStartDate}
        setSelectedEndDate={setSelectedEndDate}
        name="Оценка качества обслуживания"
        filterContent={
          <div className={classes.filtersRow}>
            <BranchQueueFilter
              selectedBranchIds={filterBranchIds}
              selectedQueueIds={filterQueueIds}
              onBranchChange={setFilterBranchIds}
              onQueueChange={setFilterQueueIds}
              singleQueue
              queueIdsFilter={queueIdsFilter}
              compact
            />
            <div className={classes.employeeSelect}>
              <div className={classes.employeeLabel}>Сотрудник</div>
              <ReactSelect<EmployeeOption, false>
                className={classes.employeeControl}
                placeholder="Все сотрудники"
                noOptionsMessage={() => 'Нет сотрудников'}
                isDisabled={!isQueueSelected || isLoadingEmployeesForFilter}
                isClearable={false}
                value={selectedEmployeeOption}
                options={employeeOptions}
                onChange={(opt) => setSelectedEmployeeId(opt?.value ?? null)}
                styles={selectStyles}
                menuPlacement="bottom"
                menuPosition="fixed"
                menuPortalTarget={menuPortalTarget}
                maxMenuHeight={240}
              />
            </div>
          </div>
        }
      />

      {isDateRangeInvalid && (
        <div className={classes.dateError}>
          Дата начала не может быть позже даты окончания
        </div>
      )}

      {!isQueueSelected ? (
        <div className={classes.emptyState}>
          Выберите очередь в фильтре, чтобы отобразить данные
        </div>
      ) : (
        <QualityReportTable
          employees={qualityReportData?.employees ?? []}
          totals={
            qualityReportData?.totals ?? {
              employeeName: 'Итого по очереди',
              ratingsCount: null,
              averageRating: null,
              score1: null,
              score2: null,
              score3: null,
              score4: null,
              score5: null,
              isSummary: true
            }
          }
          isLoadingData={isLoadingQualityReport}
        />
      )}
    </div>
  );
};

export default QualityReportPage;
